import { useState, useEffect, useCallback } from "react";
import { RefreshCw, AlertTriangle, Flag, RotateCcw } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function LiveOps() {
  const [data, setData] = useState({ tracks: [], date: "" });
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/feed/live-ops`, { credentials: "include" });
      if (r.ok) setData(await r.json());
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const putOverride = async (fc, patch) => {
    await fetch(`${API_URL}/api/feed/track-override`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ feed_code: fc, date: data.date, ...patch }),
    });
    setMsg("Advancement updated."); load();
  };
  const putCondition = async (fc, patch) => {
    await fetch(`${API_URL}/api/feed/track-condition`, {
      method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ feed_code: fc, date: data.date, ...patch }),
    });
    setMsg("Track condition updated."); load();
  };

  return (
    <div className="space-y-5" data-testid="live-ops-panel">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1">Live Ops</h1>
          <p className="text-sm text-slate-400">Day-of controls for <span className="text-emerald-400 font-mono">{data.date || "today"}</span>. Everything here resets at 4:00 AM ET.</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-sm" data-testid="live-ops-refresh">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>
      {msg && <div className="bg-emerald-500/10 border border-emerald-500/40 text-emerald-400 px-3 py-2 rounded-lg text-sm" data-testid="live-ops-msg">{msg}</div>}

      {loading ? (
        <div className="text-slate-400 py-8 text-center">Loading today's tracks…</div>
      ) : data.tracks.length === 0 ? (
        <div className="text-slate-400 py-8 text-center">No running tracks for this date.</div>
      ) : (
        <div className="space-y-3">
          {data.tracks.map((t) => {
            const raceNums = (t.races || []).map((r) => r.raceNumber);
            return (
              <div key={t.feed_code} className={`rounded-xl border p-4 ${t.stuck ? "border-amber-500/60 bg-amber-500/5" : "border-slate-800 bg-slate-900"}`} data-testid={`live-ops-track-${t.feed_code}`}>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                  <div className="flex items-center gap-2">
                    <span className="text-white font-semibold">{t.code}</span>
                    <span className="text-slate-500 text-sm">{t.name}</span>
                    {t.stuck && <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/50" data-testid={`live-ops-stuck-${t.feed_code}`}><AlertTriangle size={11} /> possibly stuck</span>}
                    {t.override.finished && <span className="px-2 py-0.5 text-xs rounded-full bg-slate-700 text-slate-300">finished</span>}
                  </div>
                  <div className="text-xs text-slate-400 flex gap-4">
                    <span>Last cycled: <b className="text-slate-200">{t.lastCycled ?? "—"}</b></span>
                    <span>Showing next: <b className="text-slate-200">{t.override.finished ? "none" : (t.computedNext ?? "—")}</b></span>
                    {t.override.next_race != null && <span className="text-sky-400">override → R{t.override.next_race}</span>}
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">Next race:</span>
                    <select
                      value={t.override.next_race ?? ""}
                      onChange={(e) => putOverride(t.feed_code, { next_race: e.target.value ? Number(e.target.value) : null })}
                      className="bg-slate-800 border border-slate-700 rounded-lg px-2 py-1 text-sm text-white"
                      data-testid={`live-ops-next-${t.feed_code}`}>
                      <option value="">(auto)</option>
                      {raceNums.map((n) => <option key={n} value={n}>R{n}</option>)}
                    </select>
                    <button onClick={() => putOverride(t.feed_code, { finished: true, next_race: null })}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-slate-700 hover:bg-slate-600 text-slate-200" data-testid={`live-ops-finish-${t.feed_code}`}>
                      <Flag size={12} /> Mark all finished
                    </button>
                    <button onClick={() => putOverride(t.feed_code, { clear: true })}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs bg-slate-800 hover:bg-slate-700 text-slate-400" data-testid={`live-ops-clear-${t.feed_code}`}>
                      <RotateCcw size={12} /> Clear
                    </button>
                  </div>

                  <div className="flex items-center gap-1.5 ml-auto">
                    <span className="text-xs text-slate-500 mr-1">Condition:</span>
                    {(() => {
                      const cond = t.condition || { off: false, sloppy: false };
                      const isDefault = !cond.off && !cond.sloppy;
                      const btn = (active, activeCls, testid, label, onClick) => (
                        <button key={testid} onClick={onClick}
                          className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition ${active ? activeCls : "bg-slate-800 text-slate-400 border-slate-700 hover:bg-slate-700"}`}
                          data-testid={testid}>
                          {label}
                        </button>
                      );
                      return [
                        btn(isDefault, "bg-emerald-600/30 text-emerald-300 border-emerald-500/60",
                          `live-ops-cond-default-${t.feed_code}`, "Fast / Firm",
                          () => putCondition(t.feed_code, { off: false, sloppy: false })),
                        btn(cond.off, "bg-amber-600/30 text-amber-300 border-amber-500/60",
                          `live-ops-cond-off-${t.feed_code}`, "Off (Turf Off)",
                          () => putCondition(t.feed_code, { off: !cond.off, sloppy: cond.sloppy })),
                        btn(cond.sloppy, "bg-rose-600/30 text-rose-300 border-rose-500/60",
                          `live-ops-cond-sloppy-${t.feed_code}`, "Sloppy / Muddy",
                          () => putCondition(t.feed_code, { off: cond.off, sloppy: !cond.sloppy })),
                      ];
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
